import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, CheckCircle, ChevronRight, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const WORKSHOPS = [
  {
    id: 'ws1_unwritten_rules', number: 1, title: 'Unwritten Rules', subtitle: 'What Nobody Tells You About Work',
    color: '#E86C3D', day: 'Day 1',
    overview: 'Every job has two sets of rules. The ones in the handbook — and the ones nobody tells you. The unwritten ones are the ones that get you promoted, ignored, included, or pushed out.',
    takeaways: ['Every workplace has unwritten rules. Spot them fast, and you move smarter.', 'Your first 30 days create your reputation. Be intentional.', 'The people who\'ve been there longest know things your supervisor doesn\'t.', 'Office drama is a trap. Stay neutral for the first month.', 'Reading the room is a skill. Practice it before you need it.'],
    activity_title: 'My Day 1 Game Plan',
    activity_instruction: 'Write down 5 unwritten rules you\'re committing to follow on Day 1. Be specific. Make them yours.',
    fields: [
      { key: 'rule1', label: 'Rule 1 — How I Show Up', placeholder: 'Example: I\'m going to come in at least 10 minutes early every day for the first 30 days.', hint: 'Time, presentation, energy. What\'s your non-negotiable for the first 30 days?' },
      { key: 'rule2', label: 'Rule 2 — How I Communicate', placeholder: 'Example: When my supervisor texts me, I\'m responding within 30 minutes — not 3 hours.', hint: 'Email tone, in-person tone, response time. What are you locking in?' },
      { key: 'rule3', label: 'Rule 3 — How I Read the Room', placeholder: 'Example: I\'m watching who my supervisor goes to first when something needs to get done.', hint: 'What specifically are you watching for in your first 48 hours?' },
      { key: 'rule4', label: 'Rule 4 — How I Handle Drama', placeholder: 'Example: When coworkers start talking about other coworkers, I\'m finding a reason to step away.', hint: 'When you see office tension brewing, what\'s your move?' },
      { key: 'rule5', label: 'Rule 5 — The Rule That\'s Just for Me', placeholder: 'Example: I have a habit of checking my phone every 5 minutes — I\'m keeping it in my bag until lunch.', hint: 'Pick the unwritten rule you know is your weak spot — and commit to flipping it.' },
    ]
  },
  {
    id: 'ws2_breaking_the_script', number: 2, title: 'Breaking the Script', subtitle: 'Stop Reacting on Autopilot',
    color: '#8B5CF6', day: 'Day 1',
    overview: 'Everybody has a script — the way you react when things get hard at work. Some scripts protect you. Most cost you. The first step to running a new script is naming the old one.',
    takeaways: ['Your reaction in hard moments is a script — and scripts can be rewritten.', 'Most scripts were inherited, not chosen.', 'The 3-second pause between trigger and reaction is where careers get built.', 'Every script has a power AND a cost.', 'Who you choose to be in the pause is who you actually are.'],
    activity_title: 'My Summer Script',
    activity_instruction: 'This is the most personal thing you\'ll write all summer. Take it seriously. Sign it when you\'re done.',
    fields: [
      { key: 'old_script', label: 'The Script I\'m Leaving Behind', placeholder: 'Example: When somebody gives me feedback, I shut down and stop talking for the rest of the day.', hint: 'The pattern that\'s been holding you back. Be honest, not aspirational.' },
      { key: 'new_script', label: 'The Script I\'m Writing This Summer', placeholder: 'Example: When somebody gives me feedback, I\'m going to pause, say "I hear you," and ask one question back.', hint: 'The version of you you\'re showing up as. Be specific about what it sounds like in a hard moment.' },
      { key: 'what_it_earns', label: 'What This Script Earns Me', placeholder: 'Example: A supervisor who actually trusts me with bigger work.', hint: 'The real wins you\'re going for this summer.' },
      { key: 'first_test', label: 'The First Real Test', placeholder: 'Example: My supervisor will probably point out something I missed, and that\'s when the new script gets tested.', hint: 'The specific moment you\'re preparing for.' },
      { key: 'commitment', label: 'My Commitment', placeholder: 'I commit to running this script all summer. I\'ll come back to this page every Sunday night.', hint: '', type: 'commitment' },
    ]
  },
  {
    id: 'ws3_the_hard_call', number: 3, title: 'The Hard Call', subtitle: 'Leadership Is a Decision You Make in Real Time',
    color: '#10B981', day: 'Day 2',
    overview: 'Leadership at your age doesn\'t look like running meetings. It looks like the moment you decide to speak up when the room goes quiet, take responsibility when something goes wrong, or step in when nobody else will.',
    takeaways: ['Leadership is showing up when it\'s easier to stay out of it.', 'You\'re already leading somewhere — name where, build from there.', 'Where you stay silent is where your growth is hiding.', 'The right time to step up never feels like the right time.', 'Small leadership reps compound. Take them every week.'],
    activity_title: 'My Leadership Audit & Step Up Plan',
    activity_instruction: 'This page is just for you. Be honest — the audit only works if you tell yourself the truth.',
    fields: [
      { key: 'stepped_up', label: 'A moment I STEPPED UP and it felt right', placeholder: 'Example: Last year my little cousin was getting bullied — I helped her figure out what to say.', hint: 'At work OR in life. Doesn\'t have to be big.' },
      { key: 'didnt_step_up', label: 'A moment I DID NOT step up — and I regret it', placeholder: 'Example: A coworker took credit for my idea in a meeting — I didn\'t say anything.', hint: 'The first one that comes to mind is the one. Write it.' },
      { key: 'what_stopped_me', label: 'What stopped me from stepping up', placeholder: 'Example: I didn\'t want to be seen as "that person" who calls people out.', hint: 'Fear? Not feeling old enough? Not wanting drama?' },
      { key: 'stepping_up_area', label: 'The area I\'m stepping up in this summer', placeholder: 'Example: At work — speaking up in team meetings instead of waiting to be called on.', hint: 'Work, family, friends, with yourself — your choice. Pick ONE.' },
      { key: 'my_move', label: 'My move when that moment shows up', placeholder: 'Example: I\'ll say "I have an idea — can I share it real quick?" and then just say it.', hint: 'What will you specifically do or say?' },
    ]
  },
  {
    id: 'ws4_negotiation_table', number: 4, title: 'The Negotiation Table', subtitle: 'Asking, Pushing Back, Speaking Up',
    color: '#F59E0B', day: 'Day 2',
    overview: 'Most young workers lose more in silence than they ever lose in conversation. You won\'t get the reference you didn\'t ask for. You won\'t fix the issue you didn\'t bring up.',
    takeaways: ['Asking is a skill, not a personality trait. You can practice it.', 'Pushing back without burning the room is possible.', 'Bringing up a concern early prevents it from becoming a crisis.', 'Strong asks have a what, a why, and a when.', '"No" is information. It\'s not the end of the conversation.'],
    activity_title: 'My Communication Playbook',
    activity_instruction: 'Three openings — written by YOU, in YOUR words. Come back to this page before every hard conversation.',
    fields: [
      { key: 'opening_ask', label: 'Opening 1 — When I Want to ASK for Something', placeholder: 'Example: "Hey, do you have 10 minutes this week? I want to talk about something I\'ve been thinking about."', hint: 'A recommendation, a project, more responsibility, time off, anything.' },
      { key: 'opening_pushback', label: 'Opening 2 — When I Want to PUSH BACK', placeholder: 'Example: "I hear what you\'re saying — and I want to share something a little different from my side."', hint: 'A schedule, feedback, getting passed over, getting overloaded.' },
      { key: 'opening_concern', label: 'Opening 3 — When I Want to BRING UP a Concern', placeholder: 'Example: "Can I be real with you for a sec? I want to bring something up before it builds up."', hint: 'Something about a coworker, a situation, something on your mind.' },
      { key: 'hard_conversation', label: 'The Conversation I\'m Having This Summer', placeholder: 'Who it\'s with, what it\'s about, when I\'m having it, which opening I\'m using.', hint: 'Commit to one specific hard conversation before the summer ends.' },
    ]
  },
  {
    id: 'ws5_trap_game', number: 5, title: 'The Trap Game', subtitle: 'Spot the Financial Traps Before They Catch You',
    color: '#EF4444', day: 'Day 3',
    overview: 'Financial traps don\'t catch you because you\'re careless. They catch you because they\'re designed for you specifically. Buy Now Pay Later, subscriptions, the "I deserve it" buy — these were built to take your money.',
    takeaways: ['Traps don\'t look like traps. That\'s the design.', 'A "need" and a "want" look the same to your wallet.', 'Spending isn\'t the enemy. Hiding wants as needs IS.', 'Small saves beat big plans. Automatic beats willpower.', 'Naming your trap is half the work.'],
    activity_title: 'My Trap Map',
    activity_instruction: 'This page only works if you tell yourself the truth.',
    fields: [
      { key: 'biggest_trap', label: 'My Biggest Spending Trap', placeholder: 'Example: Food delivery — I get DoorDash 3-4 times a week and it adds up.', hint: 'Food delivery? Sales? Concerts? The "I deserve it" buy? Subscriptions?' },
      { key: 'why_it_works', label: 'Why This Trap Works on Me Specifically', placeholder: 'Example: I tell myself I\'m "too tired to cook" but really I just don\'t want to think about it.', hint: 'Be honest about the story you tell yourself.' },
      { key: 'shoulda_saved', label: 'My Biggest "Shoulda Saved That" Moment', placeholder: 'Example: Last birthday I spent $200 on a party I barely remember.', hint: 'A specific moment from the last 6 months.' },
      { key: 'avoiding_this_summer', label: 'The ONE Trap I\'m Avoiding This Summer', placeholder: 'Example: Afterpay — I\'m not splitting any purchases into 4 payments.', hint: 'Pick the one that costs you the most.' },
      { key: 'instead', label: 'What I\'ll Do INSTEAD When the Urge Hits', placeholder: 'Example: 24-hour wait period — if I still want it the next day, I\'ll think about saving up.', hint: 'Your replacement behavior.' },
      { key: 'small_money_move', label: 'My Small Money Move — Starting This Week', placeholder: 'Example: Setting up an auto-transfer of $25 every Friday from checking to savings.', hint: 'Small beats big. Automatic beats willpower.' },
    ]
  },
  {
    id: 'ws6_financial_playbook', number: 6, title: 'Your Financial Playbook', subtitle: 'Build the Money Plan That Fits YOUR Life',
    color: '#06B6D4', day: 'Day 3',
    overview: 'Most young adults don\'t have a financial plan — not because they don\'t care, but because nobody ever taught them how. What you need first isn\'t a 10-year retirement strategy. It\'s a Playbook for the next 12 months.',
    takeaways: ['A Playbook beats a budget. Budgets fail. Plans evolve.', 'Every dollar lives in one of 3 buckets: Needs, Wants, Future Savings.', 'Future Savings isn\'t optional. It\'s where freedom comes from.', 'A 12-month vision is more powerful than a 10-year plan at your age.', 'Your first move starts THIS WEEK.'],
    activity_title: 'My Financial Playbook',
    activity_instruction: 'This is yours. It\'s not a budget. It\'s a plan you can run.',
    fields: [
      { key: 'monthly_income', label: 'Monthly Income', placeholder: 'Your rough monthly income', hint: 'The honest baseline. No judgment.', type: 'short' },
      { key: 'monthly_spending', label: 'Rough Monthly Spending', placeholder: 'How much you spend per month', hint: '', type: 'short' },
      { key: 'current_savings', label: 'Current Savings', placeholder: 'How much you have saved right now', hint: '', type: 'short' },
      { key: 'needs_pct', label: 'NEEDS Bucket (rent, food, transit) — Goal %', placeholder: 'Example: 50%', hint: 'No bucket can be 0%', type: 'short' },
      { key: 'wants_pct', label: 'WANTS Bucket (going out, fun) — Goal %', placeholder: 'Example: 30%', hint: '', type: 'short' },
      { key: 'savings_pct', label: 'FUTURE SAVINGS Bucket — Goal %', placeholder: 'Example: 20%', hint: '', type: 'short' },
      { key: 'one_year_vision', label: 'My One-Year Vision', placeholder: 'What does next summer look like? Write what you can almost see.', hint: 'What does your life look like financially in 12 months?' },
      { key: 'emergency_fund_goal', label: 'My Emergency Fund Goal', placeholder: 'Example: $500 by September', hint: '' },
      { key: 'afford_by_next_summer', label: 'The ONE Thing I Want to Afford by Next Summer', placeholder: 'Example: A used car, tuition deposit, apartment deposit', hint: '' },
      { key: 'first_move', label: 'My First Move — Starting This Week', placeholder: 'The ONE specific action you\'re taking this week.', hint: '' },
    ]
  },
];

const WorkbookPage = ({ user }) => {
  const navigate = useNavigate();
  const [selectedWS, setSelectedWS] = useState(null);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [completedWS, setCompletedWS] = useState(new Set());

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const res = await fetch(`${API}/api/workbook/my-responses`, { headers: hdr() });
        if (res.ok) {
          const data = await res.json();
          setResponses(data.responses || {});
          setCompletedWS(new Set(data.completed || []));
        }
      } catch (e) { console.error(e); }
    };
    fetchResponses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveWorksheet = async (wsId) => {
    setSaving(true);
    try {
      const wsResponses = responses[wsId] || {};
      const res = await fetch(`${API}/api/workbook/save`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({ workshop_id: wsId, responses: wsResponses })
      });
      if (res.ok) {
        toast.success('Saved!');
        const filledFields = Object.values(wsResponses).filter(v => v && v.trim()).length;
        const ws = WORKSHOPS.find(w => w.id === wsId);
        if (filledFields >= (ws?.fields?.length || 0)) {
          setCompletedWS(prev => new Set([...prev, wsId]));
        }
      }
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  // Workshop list view
  if (!selectedWS) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
        <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1a1f3a 100%)' }} className="pb-8 pt-4 px-4">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => navigate(-1)} className="p-2 rounded-xl bg-white/10 hover:bg-white/15">
                <ArrowLeft className="w-5 h-5 text-white/70" />
              </button>
              <div>
                <h1 className="text-xl font-black text-white">Orientation Workbook</h1>
                <p className="text-xs text-white/50">{completedWS.size} of {WORKSHOPS.length} worksheets completed</p>
              </div>
            </div>
            <div className="h-2 bg-white/10 rounded-full">
              <div className="h-2 bg-orange-500 rounded-full transition-all" style={{ width: `${(completedWS.size / WORKSHOPS.length) * 100}%` }} />
            </div>
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 -mt-4 space-y-3 pb-24">
          {WORKSHOPS.map((ws, i) => {
            const isDone = completedWS.has(ws.id);
            const hasStarted = responses[ws.id] && Object.values(responses[ws.id]).some(v => v && v.trim());
            return (
              <motion.div key={ws.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                onClick={() => setSelectedWS(ws)} className="cursor-pointer">
                <Card className={`p-4 rounded-2xl border transition-all hover:shadow-md ${isDone ? 'border-emerald-200 bg-emerald-50/50' : hasStarted ? 'border-orange-200' : 'border-gray-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-sm" style={{ backgroundColor: ws.color }}>
                      {isDone ? <CheckCircle className="w-6 h-6" /> : `0${ws.number}`}
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{ws.day}</p>
                      <p className="text-sm font-bold text-gray-900">{ws.title}</p>
                      <p className="text-xs text-gray-500">{ws.subtitle}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  }

  // Individual worksheet view
  const ws = selectedWS;
  const wsResponses = responses[ws.id] || {};

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
      <div className="px-4 pt-4 pb-2" style={{ background: `linear-gradient(135deg, ${ws.color} 0%, ${ws.color}CC 100%)` }}>
        <div className="max-w-lg mx-auto">
          <button onClick={() => setSelectedWS(null)} className="p-2 rounded-xl bg-white/20 hover:bg-white/30 mb-3">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <p className="text-white/60 text-xs font-bold uppercase">{ws.day} — Workshop {ws.number}</p>
          <h1 className="text-2xl font-black text-white">{ws.title}</h1>
          <p className="text-sm text-white/70 mt-1">{ws.subtitle}</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5 pb-32">
        {/* Overview */}
        <Card className="p-4 rounded-2xl">
          <h3 className="font-bold text-sm text-gray-900 mb-2">Why This Workshop Matters</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{ws.overview}</p>
        </Card>

        {/* Takeaways */}
        <Card className="p-4 rounded-2xl">
          <h3 className="font-bold text-sm text-gray-900 mb-3">Top 5 Takeaways</h3>
          <div className="space-y-2">
            {ws.takeaways.map((t, i) => (
              <div key={i} className="flex gap-2">
                <span className="text-xs font-bold text-orange-500 mt-0.5">0{i + 1}</span>
                <p className="text-xs text-gray-600">{t}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Activity Worksheet */}
        <div className="pt-2">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5" style={{ color: ws.color }} />
            <h2 className="text-lg font-bold text-gray-900">{ws.activity_title}</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4 italic">{ws.activity_instruction}</p>

          <div className="space-y-4">
            {ws.fields.map(field => (
              <div key={field.key}>
                <label className="text-sm font-bold text-gray-800 block mb-1">{field.label}</label>
                {field.hint && <p className="text-[10px] text-gray-400 mb-1">{field.hint}</p>}
                {field.type === 'short' ? (
                  <Input value={wsResponses[field.key] || ''} onChange={e => setResponses({ ...responses, [ws.id]: { ...wsResponses, [field.key]: e.target.value } })}
                    placeholder={field.placeholder} className="rounded-xl" />
                ) : (
                  <Textarea value={wsResponses[field.key] || ''} onChange={e => setResponses({ ...responses, [ws.id]: { ...wsResponses, [field.key]: e.target.value } })}
                    placeholder={field.placeholder} rows={3} className="rounded-xl text-sm" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3">
        <div className="max-w-lg mx-auto">
          <Button onClick={() => saveWorksheet(ws.id)} disabled={saving}
            className="w-full rounded-xl py-5 font-bold text-base shadow-lg" style={{ backgroundColor: ws.color }}>
            {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
            {saving ? 'Saving...' : 'Save My Responses'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WorkbookPage;
